'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RoomEvent, RpcError, Track, } from 'livekit-client';
import { useRoomContext } from '@livekit/components-react';
import { usePublishPermissions } from '@/hooks/use-publish-permissions';
import { agentCanReceiveShare, isAgentParticipant, useScreenshareAgent, } from '@/hooks/use-screenshare-peer';
import { canCaptureDisplay, isSafari } from '@/lib/screenshare-capability';
import { RPC_REQUEST_CONSENT, RPC_STOP, SCREENSHARE_PROTOCOL_VERSION, SHARE_SURFACES, isCurrentVersion, } from '@/lib/screenshare-protocol';
/**
 * TLZ-561. Every piece of screenshare state the widget owns: the capability it
 * advertises, the consent prompt, and the screen track itself.
 *
 * Two rules this hook exists to enforce, and they are the same rule twice:
 *  - consent is answered only AFTER the track is published, so a picker the caller
 *    dismissed never looks like a live share the agent then waits on; and
 *  - whatever answers the request first owns the outcome, so a share that started after
 *    the agent was told "no share" is torn down rather than left publishing. A live
 *    track the agent believes was cancelled is the same defect wearing the other face.
 */
export const DEFAULT_ALLOWED_SURFACES = [...SHARE_SURFACES];
/** Used when the agent's request does not carry its own `timeout_seconds`. */
export const DEFAULT_CONSENT_TIMEOUT_SECONDS = 30;
/** `timeout_seconds` comes from the peer, so it is bounded rather than trusted. */
export const MIN_CONSENT_TIMEOUT_SECONDS = 5;
export const MAX_CONSENT_TIMEOUT_SECONDS = 120;
/** Headroom for the answer to travel back inside the caller's `responseTimeout`. */
const RESPONSE_TRAVEL_SECONDS = 1;
/**
 * How long an agent may be gone before its departure is treated as the end of the share.
 *
 * A full reconnect disconnects every remote participant and brings them straight back,
 * and the SDK republishes the local tracks with them. Tearing a caller's screen share
 * down on a network blip is a worse outcome -- and a falser one -- than a share that
 * outlives the agent by a second and a half.
 */
export const AGENT_LEFT_GRACE_MS = 1500;
/**
 * The `displaySurface` we ask the browser for. It is a HINT and nothing more: Chrome
 * honours it, other browsers ignore it, and no browser lets a page restrict its own
 * picker. What the caller actually chose is read back with `surfaceOf`.
 */
export function preferredSurface(allowedSurfaces) {
    var _a;
    const allowList = (allowedSurfaces === null || allowedSurfaces === void 0 ? void 0 : allowedSurfaces.length) ? allowedSurfaces : DEFAULT_ALLOWED_SURFACES;
    return (_a = SHARE_SURFACES.find((surface) => allowList.includes(surface))) !== null && _a !== void 0 ? _a : 'browser';
}
/**
 * The surface the caller actually picked, read off the published track. This -- not the
 * surface we asked for -- is what the agent is told and what the audit row records.
 */
export function surfaceOf(publication) {
    var _a, _b, _c;
    const settings = (_c = (_b = (_a = publication.track) === null || _a === void 0 ? void 0 : _a.mediaStreamTrack) === null || _b === void 0 ? void 0 : _b.getSettings) === null || _c === void 0 ? void 0 : _c.call(_b);
    const surface = settings === null || settings === void 0 ? void 0 : settings.displaySurface;
    return surface && SHARE_SURFACES.includes(surface) ? surface : undefined;
}
/** The agent's requested scope, narrowed by what org policy allows. */
export function negotiateSurfaces(requested, allowedSurfaces) {
    const allowList = (allowedSurfaces === null || allowedSurfaces === void 0 ? void 0 : allowedSurfaces.length) ? allowedSurfaces : DEFAULT_ALLOWED_SURFACES;
    if (!(requested === null || requested === void 0 ? void 0 : requested.length)) {
        return allowList;
    }
    const narrowed = allowList.filter((surface) => requested.includes(surface));
    // An empty intersection means the agent asked for something policy forbids. Offering
    // the policy list is the honest fallback; the picker is the caller's choice anyway.
    return narrowed.length ? narrowed : allowList;
}
/**
 * How long the prompt may stand. Bounded at both ends because `timeout_seconds` arrives
 * from the peer, and capped again by the caller's own `responseTimeout` -- LiveKit's
 * default is 10s, and a prompt that outlives the window the agent is listening on
 * produces an answer nobody receives while the caller believes they are still deciding.
 */
export function askedWindowSeconds(requestedSeconds) {
    const requested = Number(requestedSeconds);
    // An absent or unusable `timeout_seconds` means the default was asked for, not NaN.
    return Number.isFinite(requested) && requested > 0 ? requested : DEFAULT_CONSENT_TIMEOUT_SECONDS;
}
export function consentWindowSeconds(requestedSeconds, responseTimeoutMs) {
    const asked = askedWindowSeconds(requestedSeconds);
    let seconds = Math.min(Math.max(asked, MIN_CONSENT_TIMEOUT_SECONDS), MAX_CONSENT_TIMEOUT_SECONDS);
    const window = Number(responseTimeoutMs);
    if (Number.isFinite(window) && window > 0) {
        const usable = Math.floor(window / 1000) - RESPONSE_TRAVEL_SECONDS;
        if (usable > 0) {
            seconds = Math.min(seconds, usable);
        }
    }
    return seconds;
}
function parsePayload(raw) {
    try {
        const parsed = JSON.parse(raw !== null && raw !== void 0 ? raw : '{}');
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
    }
    catch (_a) {
        return {};
    }
}
export function useScreenshareSession(options = {}) {
    const room = useRoomContext();
    // Evaluated once per session: the browser cannot grow the ability mid-call.
    const capable = useMemo(() => canCaptureDisplay(), []);
    const { agentReady, allowedSurfaces } = useScreenshareAgent();
    // Whether THIS participant may publish a screen right now. The token never grants it;
    // the worker widens the permission after the resolver approves the session, and the
    // SDK reports that as ParticipantPermissionsChanged, which this hook re-renders on.
    const { screenShare: enabled } = usePublishPermissions();
    const [isSharing, setIsSharing] = useState(false);
    const [consentRequest, setConsentRequest] = useState(null);
    const pendingRef = useRef(null);
    const enabledRef = useRef(enabled);
    const allowedSurfacesRef = useRef(allowedSurfaces);
    const promptSurfacesRef = useRef(DEFAULT_ALLOWED_SURFACES);
    const onStoppedRef = useRef(options.onStopped);
    const onCallerConsentRef = useRef(options.onCallerConsent);
    /** Set while this hook is the one taking the track down, so the reason is not guessed. */
    const stopReasonRef = useRef(null);
    /** True while a browser picker is open, so a second accept cannot open a second one. */
    const capturingRef = useRef(false);
    /** How the outstanding request was answered, when something other than the caller answered it. */
    const settledResultRef = useRef(null);
    /** Pending "the agent has gone" check; see AGENT_LEFT_GRACE_MS. */
    const agentLeftTimerRef = useRef(null);
    useEffect(() => {
        enabledRef.current = enabled;
    }, [enabled]);
    useEffect(() => {
        allowedSurfacesRef.current = allowedSurfaces;
    }, [allowedSurfaces]);
    useEffect(() => {
        onStoppedRef.current = options.onStopped;
    }, [options.onStopped]);
    useEffect(() => {
        onCallerConsentRef.current = options.onCallerConsent;
    }, [options.onCallerConsent]);
    /** Resolve the outstanding RPC exactly once and take the overlay down. */
    const settle = useCallback((response) => {
        const pending = pendingRef.current;
        pendingRef.current = null;
        settledResultRef.current = response.result;
        setConsentRequest(null);
        if (pending === null || pending === void 0 ? void 0 : pending.timer) {
            clearTimeout(pending.timer);
        }
        pending === null || pending === void 0 ? void 0 : pending.resolve(response);
    }, []);
    const accept = useCallback(async (allowedSurfaces) => {
        const localParticipant = room.localParticipant;
        try {
            const pub = await localParticipant.setScreenShareEnabled(true, Object.assign({ 
                // Tab audio is never captured: the caller is already on a voice call and
                // publishing their tab's audio would echo it back into the room.
                audio: false, video: { displaySurface: preferredSurface(allowedSurfaces) }, contentHint: 'text', selfBrowserSurface: 'exclude', surfaceSwitching: 'include' }, (isSafari() ? {} : { resolution: { width: 1920, height: 1080, frameRate: 3 } })));
            if (!pub)
                return { v: SCREENSHARE_PROTOCOL_VERSION, result: 'failed', reason: 'no_publication' };
            return {
                v: SCREENSHARE_PROTOCOL_VERSION,
                result: 'granted',
                surface: surfaceOf(pub),
                track_sid: pub.trackSid,
            };
        }
        catch (err) {
            const name = err === null || err === void 0 ? void 0 : err.name;
            // The caller dismissed the picker. That is a decision, not a fault.
            const result = name === 'NotAllowedError' ? 'cancelled' : 'failed';
            return { v: SCREENSHARE_PROTOCOL_VERSION, result, reason: name };
        }
    }, [room]);
    const stopShare = useCallback(async (reason = 'caller_stop') => {
        stopReasonRef.current = reason;
        try {
            const pub = await room.localParticipant.setScreenShareEnabled(false);
            // The SDK emits the unpublish synchronously inside that call, and the listener
            // consumes the reason. No publication came back means there was nothing to take
            // down, no event fired, and nothing was reported -- so the reason must be dropped
            // here. A latched reason would be spent on the NEXT stop, attributing the caller's
            // own browser-bar stop to whoever asked for this one.
            if (!pub) {
                stopReasonRef.current = null;
            }
            setIsSharing(false);
            return true;
        }
        catch (err) {
            // A stop that did not happen is not reported as one: the track may still be
            // live, and `isSharing` has to keep saying so. Nothing here may end or degrade
            // the audio session, so the failure is logged rather than thrown.
            stopReasonRef.current = null;
            console.warn('[screenshare] could not stop the screen track', err);
            return false;
        }
    }, [room]);
    /** Caller-initiated share, with no agent request outstanding. */
    const startShare = useCallback(async () => {
        var _a;
        if (!enabledRef.current) {
            return { v: SCREENSHARE_PROTOCOL_VERSION, result: 'failed', reason: 'not_permitted' };
        }
        if (!capable) {
            return {
                v: SCREENSHARE_PROTOCOL_VERSION,
                result: 'unsupported',
                reason: 'no_display_capture',
            };
        }
        const response = await accept(allowedSurfacesRef.current);
        setIsSharing(response.result === 'granted');
        // Announced only from here: the two returns above never opened a picker, so there
        // is no consent outcome to report.
        (_a = onCallerConsentRef.current) === null || _a === void 0 ? void 0 : _a.call(onCallerConsentRef, response);
        return response;
    }, [accept, capable]);
    const acceptConsent = useCallback(async () => {
        var _a, _b;
        const pending = pendingRef.current;
        // One picker at a time. A second accept would open a second capture whose publication
        // the SDK resolves to the same track, and the loser of the ownership check below would
        // then tear down the winner's live share -- the very defect this check exists to stop.
        // The overlay disables the button while capturing; this makes that a convenience
        // rather than the thing the invariant rests on.
        if (!pending || capturingRef.current) {
            return;
        }
        capturingRef.current = true;
        try {
            // Stop the countdown the moment the caller answers. A timeout firing behind an open
            // picker would answer the agent twice, and the second answer would be a lie.
            if (pending.timer) {
                clearTimeout(pending.timer);
                pending.timer = null;
            }
            setConsentRequest((request) => (request ? Object.assign(Object.assign({}, request), { capturing: true }) : request));
            const response = await accept(promptSurfacesRef.current);
            // Whoever answered first owns the outcome. The agent's `screenshare.stop`, and the
            // panel closing, can both land while the picker is open -- and by then the agent has
            // been told there is no share. So there must not be one: a track that arrives after
            // that answer is taken straight back down rather than left publishing a screen the
            // agent believes was never shared.
            if (pendingRef.current !== pending) {
                if (response.result === 'granted') {
                    const stopped = await stopShare(settledResultRef.current === 'declined' ? 'caller_stop' : 'agent_end');
                    if (!stopped) {
                        // Unpublishing failed, so stop the capture at the source instead. Ending a
                        // MediaStreamTrack cannot fail the way an SDK round trip can, and a stopped
                        // track is a dead capture whatever the room state says.
                        try {
                            (_b = (_a = room.localParticipant.getTrackPublication(Track.Source.ScreenShare)) === null || _a === void 0 ? void 0 : _a.track) === null || _b === void 0 ? void 0 : _b.stop();
                        }
                        catch (err) {
                            console.error('[screenshare] could not stop the underlying capture', err);
                        }
                        console.error('[screenshare] a screen track was published after the request was already answered; the capture was stopped at the source');
                    }
                }
                // Only clear an overlay that is still this request's. A newer request may already
                // be on screen, and tearing it off would leave the caller no way to answer it.
                if (pendingRef.current === null) {
                    setConsentRequest(null);
                }
                return;
            }
            setIsSharing(response.result === 'granted');
            settle(response);
        }
        finally {
            capturingRef.current = false;
        }
    }, [accept, room, settle, stopShare]);
    const declineConsent = useCallback(() => {
        settle({ v: SCREENSHARE_PROTOCOL_VERSION, result: 'declined' });
    }, [settle]);
    // Agent -> widget: "may I see your screen?"
    const handleRequestConsent = useCallback(async (data) => {
        // The SDK requires a string return; every answer goes back through here.
        const respond = (response) => JSON.stringify(response);
        const payload = parsePayload(data === null || data === void 0 ? void 0 : data.payload);
        if (!isCurrentVersion(payload)) {
            // A mismatched peer is refused rather than guessed at.
            return respond({
                v: SCREENSHARE_PROTOCOL_VERSION,
                result: 'failed',
                reason: 'protocol_version_mismatch',
            });
        }
        // The permission is the enforcement point, but a caller whose session has not been
        // widened must never see the prompt -- let alone the browser's own picker -- only
        // for the publish to be refused afterwards.
        if (!enabledRef.current) {
            return respond({
                v: SCREENSHARE_PROTOCOL_VERSION,
                result: 'failed',
                reason: 'not_permitted',
            });
        }
        // No picker is opened on a device that cannot capture: the caller is never asked
        // to agree to something that would only fail afterwards.
        if (!capable) {
            return respond({
                v: SCREENSHARE_PROTOCOL_VERSION,
                result: 'unsupported',
                reason: 'no_display_capture',
            });
        }
        if (pendingRef.current) {
            return respond({
                v: SCREENSHARE_PROTOCOL_VERSION,
                result: 'failed',
                reason: 'already_pending',
            });
        }
        const surfaces = negotiateSurfaces(payload.scope, allowedSurfacesRef.current);
        const asked = askedWindowSeconds(payload.timeout_seconds);
        const timeoutSeconds = consentWindowSeconds(payload.timeout_seconds, data === null || data === void 0 ? void 0 : data.responseTimeout);
        if (timeoutSeconds < asked) {
            // Compared against the resolved ask, not the raw field: an absent `timeout_seconds`
            // is NaN, and `NaN > 9` is false, which would truncate the default in silence.
            console.warn(`[screenshare] consent prompt shortened to ${timeoutSeconds}s from ${asked}s; the agent is listening for only ${data === null || data === void 0 ? void 0 : data.responseTimeout}ms`);
        }
        promptSurfacesRef.current = surfaces;
        return new Promise((resolve) => {
            const timer = setTimeout(() => {
                settle({ v: SCREENSHARE_PROTOCOL_VERSION, result: 'timeout' });
            }, timeoutSeconds * 1000);
            pendingRef.current = {
                resolve: (response) => resolve(respond(response)),
                timer,
            };
            setConsentRequest({
                surfaces,
                timeoutSeconds,
                expiresAt: Date.now() + timeoutSeconds * 1000,
                capturing: false,
            });
        });
    }, [capable, settle]);
    // Agent -> widget: "stop sharing".
    const handleStop = useCallback(async (data) => {
        const versionOk = isCurrentVersion(parsePayload(data === null || data === void 0 ? void 0 : data.payload));
        // A prompt still on screen means no share ever started. Close it and tell the agent
        // the request ended. `cancelled` is the caller's own dismissal of the picker, so an
        // agent-side withdrawal is `failed` with the reason that says who ended it --
        // attributing it to the caller would put a plausible falsehood in the audit row.
        if (pendingRef.current) {
            settle({ v: SCREENSHARE_PROTOCOL_VERSION, result: 'failed', reason: 'agent_end' });
        }
        // Deliberately asymmetric with requestConsent: refusing to START on a version we do
        // not understand is safe, refusing to STOP is not. A privacy control fails toward
        // stopping, so the track goes down first and the mismatch is reported after.
        const stopped = await stopShare('agent_end');
        // A still-live track is the more consequential fact, so it is the one reported: a
        // peer with both problems needs to know the screen is still being shared far more
        // than it needs to know its version stamp was wrong.
        if (!stopped) {
            // Never answer `stopped: true` for a stop that did not happen.
            throw new RpcError(RpcError.ErrorCode.APPLICATION_ERROR, 'screenshare.stop: the screen track could not be stopped');
        }
        if (!versionOk) {
            throw new RpcError(RpcError.ErrorCode.UNSUPPORTED_VERSION, 'screenshare.stop: protocol_version_mismatch');
        }
        const response = { v: SCREENSHARE_PROTOCOL_VERSION, stopped: true };
        return JSON.stringify(response);
    }, [settle, stopShare]);
    // A4: the capability attribute (ATTR_CAPABLE) used to be published from here via
    // room.localParticipant.setAttributes() on RoomEvent.Connected. It is now stamped into
    // the access token instead (app/api/connection-details/route.ts), because doing it from
    // the client required granting `canUpdateOwnMetadata` to an anonymous participant on a
    // public CORS-`*` token route — and LiveKit's server filters no attribute keys, so that
    // permission also let a tampered page write the `caller` / `sip.*` keys the worker reads
    // as caller identity. The worker sees the identical attribute either way.
    useEffect(() => {
        if (!room) {
            return;
        }
        const register = (method, handler) => {
            // registerRpcMethod throws if the name is already taken (e.g. a re-registration
            // after a hot reload), and a throw here would take the whole widget down.
            try {
                room.unregisterRpcMethod(method);
                room.registerRpcMethod(method, handler);
            }
            catch (err) {
                console.warn(`[screenshare] could not register ${method}`, err);
            }
        };
        register(RPC_REQUEST_CONSENT, handleRequestConsent);
        register(RPC_STOP, handleStop);
        return () => {
            try {
                room.unregisterRpcMethod(RPC_REQUEST_CONSENT);
                room.unregisterRpcMethod(RPC_STOP);
            }
            catch (err) {
                console.warn('[screenshare] could not unregister the RPC handlers', err);
            }
        };
    }, [room, handleRequestConsent, handleStop]);
    // The browser's own "Stop sharing" bar ends the share without going through this hook.
    // Track publication state is the source of truth, and this is the only place a stop is
    // reported, so one ended share produces exactly one `onStopped`.
    useEffect(() => {
        if (!room) {
            return;
        }
        const onUnpublished = (publication) => {
            var _a, _b;
            if (publication.source !== Track.Source.ScreenShare) {
                return;
            }
            // A reconnect is not a stop. `republishAllTracks` unpublishes every local track and
            // publishes it straight back, so an unpublish that lands while the room is not
            // connected would report a share that is still live -- and, because the track
            // returns, a SECOND stop would then be reported when it really does end. The room
            // ending is handled by `onDisconnected` below.
            if (room.state !== 'connected') {
                return;
            }
            setIsSharing(false);
            const reason = (_a = stopReasonRef.current) !== null && _a !== void 0 ? _a : 'browser_stop';
            stopReasonRef.current = null;
            (_b = onStoppedRef.current) === null || _b === void 0 ? void 0 : _b.call(onStoppedRef, reason);
        };
        // The session ending is not a stop anyone can act on: there is no room left to notify.
        const onDisconnected = () => setIsSharing(false);
        room.on(RoomEvent.LocalTrackUnpublished, onUnpublished);
        room.on(RoomEvent.Disconnected, onDisconnected);
        return () => {
            room.off(RoomEvent.LocalTrackUnpublished, onUnpublished);
            room.off(RoomEvent.Disconnected, onDisconnected);
        };
    }, [room]);
    // A share whose only viewer has gone is over. It is routed through `stopShare` rather
    // than reported directly, so the end travels the SAME single path as every other stop:
    // one unpublish, one `onStopped`, one notification. Reporting it here as well would
    // double-count exactly the share the caller most needs told about accurately.
    useEffect(() => {
        if (!room) {
            return;
        }
        const clearPending = () => {
            if (agentLeftTimerRef.current) {
                clearTimeout(agentLeftTimerRef.current);
                agentLeftTimerRef.current = null;
            }
        };
        /**
         * Everything is re-checked rather than assumed, because both callers exist precisely
         * for the case where the situation changes before this runs.
         *
         * `stopReasonRef` is the single-notify guard: it is non-null from the moment a stop is
         * asked for until the unpublish listener consumes it, so a second call inside that
         * window cannot start a second teardown of the same share.
         */
        const reportAgentGone = () => {
            var _a, _b;
            const stillAway = !Array.from((_b = (_a = room.remoteParticipants) === null || _a === void 0 ? void 0 : _a.values()) !== null && _b !== void 0 ? _b : []).some((remote) => isAgentParticipant(remote));
            if (room.state !== 'connected' || !stillAway || stopReasonRef.current) {
                return;
            }
            // Nothing published means nothing to stop: `setScreenShareEnabled(false)` would
            // emit no unpublish, and a stop would be reported for a share that never was.
            if (!room.localParticipant.getTrackPublication(Track.Source.ScreenShare)) {
                return;
            }
            void stopShare('agent_left');
        };
        const onParticipantDisconnected = (participant) => {
            if (!isAgentParticipant(participant) || agentLeftTimerRef.current) {
                return;
            }
            agentLeftTimerRef.current = setTimeout(() => {
                agentLeftTimerRef.current = null;
                reportAgentGone();
            }, AGENT_LEFT_GRACE_MS);
        };
        // A reconnect that outlasts the grace window would otherwise lose the departure
        // entirely: the timer fires while the room is still reconnecting, declines to report,
        // and nulls itself -- and the SDK does not re-emit ParticipantDisconnected for a
        // participant that never came back, so nothing would ever re-arm. By the time
        // Reconnected is emitted the connection state is already `connected` and
        // `remoteParticipants` has been repopulated from the join response, so absence here is
        // the real thing rather than a gap mid-restart.
        const onReconnected = () => {
            clearPending();
            reportAgentGone();
        };
        room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
        room.on(RoomEvent.Reconnected, onReconnected);
        room.on(RoomEvent.Disconnected, clearPending);
        return () => {
            room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
            room.off(RoomEvent.Reconnected, onReconnected);
            room.off(RoomEvent.Disconnected, clearPending);
            clearPending();
        };
    }, [room, stopShare]);
    // A share the agent no longer accepts is over too. An agent-to-agent transfer inside the
    // worker keeps the SAME participant, so the departure path above never fires for it:
    // when the receiving agent's policy is off, the worker retracts `telzino.screenshare.enabled`
    // on the participant that is still here. `canShare` then hides the control, but a share
    // already published would keep running -- and the banner keep saying the caller is
    // sharing -- with no agent reading it. Same single stop path as every other end.
    //
    // Only an agent that is still PRESENT but no longer ready counts. An agent that is gone
    // also turns `agentReady` false, and that is the departure path's to judge, grace window
    // and all: acting on it here would tear a share down on a reconnect blip.
    const agentReadyRef = useRef(agentReady);
    /**
     * A withdrawal seen while the room was not connected. The effect below runs only when
     * `agentReady` changes, so without this a retraction that lands mid-reconnect would be
     * recorded and never acted on: nothing re-runs the effect once the room is back.
     */
    const withdrawalDeferredRef = useRef(false);
    /**
     * Stop the share if the agent in the room no longer accepts it. Re-checks everything, and
     * reads readiness off the room itself rather than `agentReady`: after a reconnect the SDK
     * repopulates `remoteParticipants` without emitting the events that state is derived from,
     * so the state can still describe the room as it was before the restart.
     */
    const stopIfAgentWithdrew = useCallback(() => {
        var _a, _b;
        if (!room || room.state !== 'connected' || stopReasonRef.current) {
            return;
        }
        const remotes = Array.from((_b = (_a = room.remoteParticipants) === null || _a === void 0 ? void 0 : _a.values()) !== null && _b !== void 0 ? _b : []);
        if (remotes.some((remote) => agentCanReceiveShare(remote))) {
            return;
        }
        if (!remotes.some((remote) => isAgentParticipant(remote))) {
            return;
        }
        // Nothing published means nothing to stop, and nothing to report.
        if (!room.localParticipant.getTrackPublication(Track.Source.ScreenShare)) {
            return;
        }
        void stopShare('agent_left');
    }, [room, stopShare]);
    useEffect(() => {
        const wasReady = agentReadyRef.current;
        agentReadyRef.current = agentReady;
        if (agentReady) {
            withdrawalDeferredRef.current = false;
            return;
        }
        if (!room || !wasReady) {
            return;
        }
        if (room.state !== 'connected') {
            withdrawalDeferredRef.current = true;
            return;
        }
        stopIfAgentWithdrew();
    }, [room, agentReady, stopIfAgentWithdrew]);
    // The deferred half. By the time Reconnected is emitted the state is `connected` and
    // `remoteParticipants` has been repopulated, so the checks see the room as it now is: an
    // agent that came back ready clears the deferral above, and one that is gone is left to
    // the departure path.
    useEffect(() => {
        if (!room) {
            return;
        }
        const onReconnected = () => {
            if (!withdrawalDeferredRef.current) {
                return;
            }
            withdrawalDeferredRef.current = false;
            stopIfAgentWithdrew();
        };
        room.on(RoomEvent.Reconnected, onReconnected);
        return () => {
            room.off(RoomEvent.Reconnected, onReconnected);
        };
    }, [room, stopIfAgentWithdrew]);
    // The panel can close with a request still outstanding; the agent is told rather than
    // left waiting for a promise nobody will ever settle. A capture still in flight is
    // caught by the ownership check in acceptConsent, which runs even after unmount.
    useEffect(() => () => {
        if (pendingRef.current) {
            settle({ v: SCREENSHARE_PROTOCOL_VERSION, result: 'failed', reason: 'widget_closed' });
        }
    }, [settle]);
    return {
        isSharing,
        agentReady,
        // All three conditions, and the agent one is not decoration: without it a caller can
        // start a share nobody is listening for, producing a screen track against no consent
        // record at all.
        canShare: enabled && capable && agentReady,
        consentRequest,
        acceptConsent,
        declineConsent,
        startShare,
        stopShare,
    };
}
export default useScreenshareSession;
